import {
  Avatar,
  Box,
  Button,
  ButtonBase,
  InputLabel,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { CustomInput } from "global/components";
import {
  handleErrorMessage,
  isTruthy,
  getNameInitials,
  openSuccessNotification,
} from "helpers/methods";
import {
  fetchProfileDetails,
  initiateAccountClosure,
  updateProfileDetails,
  updateProfilePicture,
} from "../SettingsService";
import { useEffect, useState } from "react";
import { CustomContactNumberInput } from "global/components/CustomContactNumberInput/CustomContactNumberInput";
import {
  getProfileFormData,
  validateForProfileForm,
} from "../General/GeneralTypesAndValidation";
import notifiers from "global/constants/NotificationConstants";
import {
  updateUserName,
  updateEmail,
  selectId,
  updateProfileImage,
  logOutAction,
} from "redux/authSlice";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import ProfileImageModal from "./components/ProfileImageModal";
import { UserData } from "models/interfaces";
import { removeProfilePicture } from "./MyProfileService";
import defaultProfile from "../../../assets/icons1/avatars/default.png";
import { useTitle } from "utils/UseTitle";
import title from "global/constants/TitleConstants";
import { isAdmin } from "layouts/Config";
import DeleteAccountFlow from "./components/DeleteAccountFlow";
import { removeDeviceId } from "screens/Auth/AuthService";
import history from "utils/history";
import viewpaths from "global/constants/ViewPathConstants";
import SettingsLayout from "../components/SettingsLayout";
import CenteredAuthenticatedLayout from "layouts/CenteredAuthenticated/CenteredAuthenticatedLayout";
import {
  fetchSelfSignInitialsImage,
  fetchSelfSignSignImage,
} from "screens/SignRequest/SignRequestService";
import SignaturesAndInitialsCard, {
  SignaturesAndInitialsSkeleton,
} from "./components/SignaturesAndInitialsCard";
import myProfileStyles from "./MyProfile.styles";
import profileTokens from "./MyProfile.tokens";
import { RoleShieldIcon } from "./components/ProfileIcons";
import { useAriaLive } from "hooks/use-aria-live";
import { LiveRegion } from "components/live-region";

const styles = myProfileStyles;

const MyProfile = () => {
  useTitle(title.PROFILE);
  const dispatch = useAppDispatch();
  const userId = useAppSelector(selectId);
  const [loader, setLoader] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profile, setProfile] = useState<UserData>(getProfileFormData());
  const [isChecked, setIsChecked] = useState(false);
  const [profileImagePreview, setProfileImagePreview] = useState<
    string | undefined
  >();
  const [profileImageModalOpen, setProfileImageModalOpen] = useState(false);
  const [view, setView] = useState<"profile" | "delete">("profile");
  const [closureLoading, setClosureLoading] = useState<boolean>(false);
  const { announcement, priority, announce } = useAriaLive();

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoader(true);
      const [response, signRes, initialsRes] = await Promise.all([
        fetchProfileDetails(),
        fetchSelfSignSignImage().catch(() => null),
        fetchSelfSignInitialsImage().catch(() => null),
      ]);
      const parsedProfile = getProfileFormData(response);

      const processBlob = (res: any, fieldName: string) => {
        if (res) {
          const reader = new FileReader();
          reader.readAsDataURL(res);
          reader.onloadend = () => {
            const url: any = reader.result;
            if (isTruthy(url.split(",")[1])) {
              setProfile((prev: any) => ({ ...prev, [fieldName]: url }));
            }
          };
        }
      };

      processBlob(signRes, "signatureImage");
      processBlob(initialsRes, "initialsImage");

      setProfile(parsedProfile);
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setLoader(false);
    }
  };

  /** Raw picture the user has, or an empty string when there is none. */
  const getAvatarSrc = () => {
    const profilePicture = profileImagePreview || profile.profilePicture;
    if (!profilePicture || profilePicture === "data:image/png;base64,") {
      return "";
    }
    if (
      profilePicture.startsWith("/") ||
      profilePicture.startsWith("http") ||
      profilePicture.startsWith("data:image")
    ) {
      return profilePicture;
    }
    return "data:image/png;base64," + profilePicture;
  };

  const getImageUrl = () => getAvatarSrc() || defaultProfile;

  const handleOnChangeInputField = (event: React.ChangeEvent<any>) => {
    setProfile({
      ...profile,
      [event.target.name]: {
        value: event.target.value,
        error: "",
      },
    });
  };

  const handleValidation = () => {
    const { isValid, errors } = validateForProfileForm(profile);
    setProfile({ ...errors });
    return isValid;
  };

  const updateProfile = async () => {
    if (!handleValidation()) return;

    try {
      setProfileSaving(true);
      const body = {
        id: userId,
        name: profile.name.value,
        email: profile.email.value,
        contactNo: profile.contactNo.value,
        role: profile.role.value,
        parentEmail: profile.parentEmail,
        roleIds: profile.roleIds.value,
      };
      await updateProfileDetails(body);
      dispatch(updateUserName(body.name));
      dispatch(updateEmail(body.email));
      openSuccessNotification(notifiers.UPDATE);
      announce("Profile updated successfully.");
    } catch (error: any) {
      handleErrorMessage(error);
      announce("Could not update profile. Please try again.", "assertive");
    } finally {
      setProfileSaving(false);
    }
  };

  const handleProfileImageModalSave = async (img: string) => {
    try {
      setLoader(true);
      setProfileImagePreview(img);
      setProfile({ ...profile, profilePicture: img });

      const formData = new FormData();
      const response = await fetch(img);
      const blob = await response.blob();
      const extension = blob.type.split("/")[1] || "png";
      formData.append("profilePicture", blob, `profile.${extension}`);

      await updateProfilePicture(formData);
      const updatedProfile = await fetchProfileDetails();

      setProfile((prev) => ({
        ...prev,
        profilePicture: updatedProfile.profilePicture,
      }));
      let imageUrl = updatedProfile.profilePicture;
      if (
        !imageUrl.startsWith("data:") &&
        !imageUrl.startsWith("http") &&
        !imageUrl.startsWith("/")
      ) {
        imageUrl = "data:image/png;base64," + imageUrl;
      }
      dispatch(updateProfileImage(imageUrl));
      announce("Profile photo updated.");
    } catch (error: any) {
      handleErrorMessage(error);
      announce("Could not update profile photo. Please try again.", "assertive");
    } finally {
      setLoader(false);
    }
  };

  const handleRemoveProfileImage = async () => {
    try {
      setProfileImagePreview(undefined);
      setProfile({ ...profile, profilePicture: "" });
      dispatch(updateProfileImage(defaultProfile));
      await removeProfilePicture();
      announce("Profile photo removed.");
    } catch (error: any) {
      handleErrorMessage(error);
      announce("Could not remove profile photo. Please try again.", "assertive");
    } finally {
      setLoader(false);
    }
  };

  const handleAccountClosure = async (currentPassword: string) => {
    try {
      setClosureLoading(true);
      const res = await initiateAccountClosure(currentPassword);
      openSuccessNotification(res.message);
      try {
        await removeDeviceId();
      } catch (err) {
        // ignore — still log out locally
      }
      dispatch(logOutAction({}));
      history.push(viewpaths.loginViewPath);
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setClosureLoading(false);
    }
  };

  /**
   * Serves two different states, so it carries no ARIA of its own: only the
   * save-in-progress caller names it. On first load it is content inside
   * `getProfileSkeleton`'s own "Loading profile" region, and a second status
   * region nested there would label the initial load as a save.
   */
  const getProfileCardSkeleton = (label?: string) => (
    <Box
      sx={styles.formCard}
      {...(label
        ? { role: "status", "aria-busy": true, "aria-label": label }
        : {})}
    >
      <Box sx={styles.profileCardBody}>
        <Box sx={styles.avatarColumn}>
          <Skeleton
            variant="circular"
            width={profileTokens.avatar.size}
            height={profileTokens.avatar.size}
          />
          <Skeleton
            variant="rounded"
            width={124}
            height={profileTokens.smallButton.height}
          />
        </Box>
        <Box sx={styles.detailsColumn}>
          <Box sx={styles.cardHeader}>
            <Skeleton variant="text" width={140} height={20} />
            <Skeleton variant="text" width="60%" height={16} />
          </Box>
          <Box sx={styles.fieldGrid}>
            {[0, 1, 2, 3].map((i) => (
              <Box key={i}>
                <Skeleton variant="text" width={70} height={14} />
                <Skeleton
                  variant="rounded"
                  width="100%"
                  height={profileTokens.field.height}
                />
              </Box>
            ))}
          </Box>
          <Box sx={styles.submitRow}>
            <Skeleton
              variant="rounded"
              width={140}
              height={profileTokens.primaryButton.height}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );

  const getProfileCard = () =>
    profileSaving ? (
      getProfileCardSkeleton("Saving profile")
    ) : (
      <Box sx={styles.formCard}>
        <Box sx={styles.profileCardBody}>
          <Box sx={styles.avatarColumn}>
            {/*
              Decorative preview only — changing the photo is handled by the
              keyboard-accessible "Change"/"Remove" buttons below, so the avatar
              itself carries no click/keyboard handler (avoids a redundant,
              unreachable control for keyboard and screen-reader users).
            */}
            <Avatar
              src={getAvatarSrc()}
              alt={
                getAvatarSrc()
                  ? `${profile.name.value || "Your"} profile photo`
                  : ""
              }
              sx={styles.avatar}
            >
              {getNameInitials(profile.name.value)}
            </Avatar>
            <Box sx={styles.avatarActions}>
              <Button
                variant="outlined"
                onClick={() => setProfileImageModalOpen(true)}
                sx={styles.smallButton}
              >
                Change
              </Button>
              <Button
                variant="outlined"
                onClick={handleRemoveProfileImage}
                sx={styles.smallDangerButton}
              >
                Remove
              </Button>
            </Box>
          </Box>

          <Box sx={styles.detailsColumn}>
            <Box sx={styles.cardHeader}>
              <Typography component="h2" sx={styles.cardTitle}>
                Profile Details
              </Typography>
              <Typography component="div" sx={styles.cardDescription}>
                Your name and contact info, shown to everyone you send
                documents to.
              </Typography>
            </Box>

            <Box sx={styles.fieldGrid}>
              <CustomInput
                id="name"
                label="Name"
                required
                placeHolder="Enter name"
                name="name"
                type="text"
                onChange={handleOnChangeInputField}
                propsToInputElement={{ minLength: 2, maxLength: 30 }}
                value={profile.name.value}
                error={profile.name.error}
                labelSx={styles.fieldLabel}
                labelSpacing={profileTokens.field.labelGap}
                sx={styles.textField}
              />
              <CustomInput
                id="email"
                label="Email"
                placeHolder="Enter email"
                value={profile.email.value}
                disabled
                labelSx={styles.mutedFieldLabel}
                labelSpacing={profileTokens.field.labelGap}
                sx={styles.textField}
              />
              <CustomContactNumberInput
                id="contact"
                name="contactNo"
                label="Phone"
                required
                value={profile.contactNo.value}
                placeHolder="Enter phone"
                onChange={(phone) => {
                  setProfile({
                    ...profile,
                    contactNo: { value: phone, error: "" },
                  });
                }}
                error={profile.contactNo.error}
                labelSx={styles.fieldLabel}
                labelSpacing={profileTokens.field.labelGap}
                sx={styles.phoneNumberField}
              />
              <Box>
                <Box
                  mb={profileTokens.field.labelGap}
                  sx={styles.roleLabelRow}
                >
                  <InputLabel sx={styles.mutedFieldLabel}>Role</InputLabel>
                </Box>
                <Box sx={styles.roleValueRow}>
                  <Box component="span" sx={styles.roleChip}>
                    <RoleShieldIcon sx={styles.roleChipIcon} />
                    {profile.role.value || "—"}
                    <Box component="span" sx={styles.roleChipSuffix}>
                      role
                    </Box>
                  </Box>
                  <Typography component="span" sx={styles.roleNote}>
                    Assigned by your organization
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box sx={styles.submitRow}>
              <Button
                variant="contained"
                onClick={updateProfile}
                disabled={profileSaving}
                sx={styles.submitButton}
              >
                Update Profile
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    );

  const getNewsLetter = () => (
    <Box sx={styles.newsletterCard}>
      <Box sx={styles.rowText}>
        <Typography component="h2" sx={styles.rowTitle}>
          Newsletter
        </Typography>
        <Typography component="div" sx={styles.cardDescription}>
          Product updates and signing tips from Inkfree, about once a month.
        </Typography>
      </Box>
      <ButtonBase
        disableRipple
        role="switch"
        aria-checked={isChecked}
        aria-label="Newsletter"
        onClick={() => setIsChecked(!isChecked)}
        sx={styles.toggleTrack(isChecked)}
      >
        <Box sx={styles.toggleKnob(isChecked)} />
      </ButtonBase>
    </Box>
  );

  const getCloseAccount = () => (
    <Box sx={styles.dangerRowCard}>
      <Box sx={styles.rowText}>
        <Typography component="h2" sx={styles.rowTitle}>
          Close your account
        </Typography>
        <Typography component="div" sx={styles.cardDescription}>
          Permanently delete this organization and all of its data. This cannot
          be undone.
        </Typography>
      </Box>
      <Button
        variant="outlined"
        onClick={() => setView("delete")}
        sx={styles.dangerButton}
      >
        Close Account
      </Button>
    </Box>
  );

  const getTermsOfPrivacy = () => (
    <Typography component="div" sx={styles.footerText}>
      By using Inkfree you agree on behalf of your organization to comply with
      Inkfree's{" "}
      <Box
        component="a"
        href="https://inkfree.app/terms/"
        target="_blank"
        rel="noreferrer"
        aria-label="Terms of Use (opens in a new tab)"
        sx={styles.footerLink}
      >
        Terms of Use
      </Box>{" "}
      and{" "}
      <Box
        component="a"
        href="https://inkfree.app/privacy-policy/"
        target="_blank"
        rel="noreferrer"
        aria-label="Privacy Policy (opens in a new tab)"
        sx={styles.footerLink}
      >
        Privacy Policy
      </Box>
      .
    </Typography>
  );

  const getProfileSkeleton = () => (
    <Stack spacing={2.5} role="status" aria-busy="true" aria-label="Loading profile">
      {getProfileCardSkeleton()}

      <SignaturesAndInitialsSkeleton />

      <Box sx={styles.newsletterCard}>
        <Box sx={styles.skeletonRowText}>
          <Skeleton variant="text" width={110} height={18} />
          <Skeleton variant="text" width="70%" height={16} />
        </Box>
        <Skeleton
          variant="rounded"
          width={profileTokens.toggle.width}
          height={profileTokens.toggle.height}
          sx={{ borderRadius: "999px", flexShrink: 0, ml: "auto" }}
        />
      </Box>

      {/* Only admins see the real card, so the skeleton matches — otherwise
          the page grows a whole box the moment loading finishes. */}
      {isAdmin() && (
        <Box sx={styles.dangerRowCard}>
          <Box sx={styles.skeletonRowText}>
            <Skeleton variant="text" width={140} height={18} />
            <Skeleton variant="text" width="80%" height={16} />
          </Box>
          <Skeleton
            variant="rounded"
            width={140}
            height={profileTokens.danger.buttonHeight}
            sx={{
              borderRadius: profileTokens.danger.buttonRadius,
              flexShrink: 0,
              ml: { xs: 0, sm: "auto" },
            }}
          />
        </Box>
      )}
    </Stack>
  );

  if (view === "delete") {
    return (
      <CenteredAuthenticatedLayout>
        <DeleteAccountFlow
          onBack={() => setView("profile")}
          onConfirm={handleAccountClosure}
          loading={closureLoading}
        />
      </CenteredAuthenticatedLayout>
    );
  }

  return (
    <SettingsLayout activeTab="My Profile" title="My Profile">
      <LiveRegion message={announcement} priority={priority} />
      {loader ? (
        getProfileSkeleton()
      ) : (
        <>
          {getProfileCard()}
          <SignaturesAndInitialsCard
            profile={profile}
            setProfile={setProfile}
          />
          {getNewsLetter()}
          {isAdmin() && getCloseAccount()}
          {getTermsOfPrivacy()}
          <ProfileImageModal
            open={profileImageModalOpen}
            onClose={() => setProfileImageModalOpen(false)}
            onSave={handleProfileImageModalSave}
            initialImage={getImageUrl()}
            maxWidth="md"
          />
        </>
      )}
    </SettingsLayout>
  );
};

export default MyProfile;
